import { useCallback, useEffect, useRef, useState } from "react";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

const BOTTOM_TOLERANCE = 48;

/**
 * Shows the agreement in a sandboxed iframe so the document's own print styles
 * can't collide with the app's global CSS, and reports when the reader has
 * actually reached the bottom — signing stays disabled until then, which is our
 * evidence that the terms were presented in full.
 *
 * iOS Safari ignores a height set on an iframe and expands it to its content,
 * so the iframe is sized to its content on purpose and the wrapper does the
 * scrolling. That behaves identically on desktop and keeps one scroll listener.
 */
export default function DocumentReader({ t, html, onReadToEnd }) {
  const scrollerRef = useRef(null);
  const iframeRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [ready, setReady] = useState(false);

  const markRead = useCallback(() => {
    setReachedEnd((already) => {
      if (!already) onReadToEnd?.();
      return true;
    });
  }, [onReadToEnd]);

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const { scrollTop, scrollHeight, clientHeight } = scroller;
    const scrollable = scrollHeight - clientHeight;

    // A document shorter than the viewport is fully visible already.
    if (scrollable <= BOTTOM_TOLERANCE) {
      setProgress(100);
      markRead();
      return;
    }

    setProgress(Math.min(100, Math.round((scrollTop / scrollable) * 100)));
    if (scrollTop >= scrollable - BOTTOM_TOLERANCE) markRead();
  }, [markRead]);

  const syncHeight = useCallback(() => {
    const iframe = iframeRef.current;
    const body = iframe?.contentDocument?.body;
    if (!iframe || !body) return;

    const height = Math.max(
      body.scrollHeight,
      iframe.contentDocument.documentElement?.scrollHeight || 0
    );
    iframe.style.height = `${height}px`;
    measure();
  }, [measure]);

  const handleLoad = useCallback(() => {
    setReady(true);
    syncHeight();
    // Fonts and the letterhead image settle after load and change the height.
    const timers = [150, 600, 1500].map((delay) =>
      setTimeout(syncHeight, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [syncHeight]);

  useEffect(() => {
    if (!ready) return undefined;
    window.addEventListener("resize", syncHeight);
    return () => window.removeEventListener("resize", syncHeight);
  }, [ready, syncHeight]);

  const scrollToBottom = () => {
    scrollerRef.current?.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "grey.300",
          bgcolor: "#f6f7fb",
        }}
      >
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            "& .MuiLinearProgress-bar": {
              bgcolor: reachedEnd ? "success.main" : "primary.main",
            },
          }}
        />

        <Box
          ref={scrollerRef}
          onScroll={measure}
          sx={{
            height: { xs: "62vh", md: "70vh" },
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
          }}
        >
          <Box
            component="iframe"
            ref={iframeRef}
            title={t("documentTitle")}
            srcDoc={html}
            scrolling="no"
            sandbox="allow-same-origin"
            onLoad={handleLoad}
            sx={{
              display: "block",
              width: "100%",
              border: 0,
              minHeight: "100%",
            }}
          />
        </Box>

        <Fade in={!reachedEnd && ready}>
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              pt: 5,
              pb: 1.5,
              display: "flex",
              justifyContent: "center",
              pointerEvents: "none",
              background:
                "linear-gradient(180deg, rgba(246,247,251,0) 0%, rgba(246,247,251,0.95) 65%)",
            }}
          >
            <Button
              size="small"
              variant="contained"
              onClick={scrollToBottom}
              startIcon={<KeyboardDoubleArrowDownIcon />}
              sx={{
                pointerEvents: "auto",
                borderRadius: 5,
                textTransform: "none",
                boxShadow: 3,
              }}
            >
              {t("scrollToBottom")}
            </Button>
          </Box>
        </Fade>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mt: 1.25, minHeight: 24 }}
      >
        {reachedEnd ? (
          <>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 18, color: "success.main" }}
            />
            <Typography
              variant="body2"
              sx={{ color: "success.main", fontWeight: 600, fontSize: 13.5 }}
            >
              {t("scrollDone")}
            </Typography>
          </>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: 13.5 }}
          >
            {t("scrollHint")} · {progress}%
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
